/**
 * "Action Required" items across modules, shared by the dashboard card and the
 * Approvals page (View all). Built from GET /dashboard/overview plus the pending
 * attendance approvals the user may act on (GET /wf/approvals).
 */
import { useQuery } from "@tanstack/react-query";
import { fetchWfApprovals } from "../Moduels/attendance/wfApi";
import { LEAVE_APPROVAL_NAV_STATE } from "../Moduels/leave/leaveQueueEvents";

const DEFAULT_BANK_ROUTE = "/employees?module=employees&needs_bank=1&step=4";
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Opens the bank step of the affected employee when the alert names one. */
function bankRoute(alerts) {
  const raw = alerts.find((a) => a.route?.includes("needs_bank"))?.route || "";
  const id = raw.match(/\/employees\/([^/?]+)\/edit/)?.[1] || raw.match(/[?&]id=([^&]+)/)?.[1];
  return id ? `/employees/${id}/edit?needs_bank=1&step=4` : DEFAULT_BANK_ROUTE;
}

/** Pending attendance approvals (OT, corrections …) the signed-in user can act on. */
export function useAttendanceApprovalCount(enabled) {
  const query = useQuery({
    queryKey: ["action-items", "attendance-approvals"],
    queryFn: () => fetchWfApprovals({ statusFilter: "pending" }),
    enabled: Boolean(enabled),
    staleTime: 30_000,
    retry: 1,
  });
  return Array.isArray(query.data) ? query.data.length : 0;
}

/**
 * @returns {Array<{key, group, tone, title, detail, label, count, onClick}>}
 *   ``count`` is how many things the item stands for (used for the Pending Actions total).
 */
export function buildActionItems(overview, navigate, { attendanceApprovals = 0, fuelPending = 0, onFuelReview } = {}) {
  const items = [];
  if (!overview) return items;
  const employees = overview.employees || {};
  const attendance = overview.attendance_today;
  const leave = overview.leave_summary;
  const readiness = overview.payroll_readiness;
  const alerts = overview.alerts || [];

  // ── Employees ──
  const missingBank = employees.missing_bank_account || 0;
  if (missingBank > 0) {
    items.push({
      key: "bank",
      group: "Employees",
      tone: "absent",
      title: `${plural(missingBank, "employee")} missing verified bank accounts`,
      detail: "Bank account details are required for salary transfer.",
      label: "Resolve",
      count: missingBank,
      onClick: () => navigate(bankRoute(alerts)),
    });
  }
  const missingContact = employees.missing_contact || 0;
  if (missingContact > 0) {
    items.push({
      key: "contact",
      group: "Employees",
      tone: "half",
      title: `${plural(missingContact, "employee")} missing email contact`,
      detail: "An email is needed for payslips and notifications.",
      label: "Update",
      count: missingContact,
      onClick: () => navigate("/employees?module=employees"),
    });
  }

  // ── Allowances ──
  if (fuelPending > 0) {
    items.push({
      key: "fuel",
      group: "Allowances",
      tone: "half",
      title: `${plural(fuelPending, "fuel bill request")} waiting for verification`,
      detail: "Check the bill number and date, then approve or reject.",
      label: "Verify",
      count: fuelPending,
      onClick: onFuelReview || (() => navigate("/approvals")),
    });
  }

  // ── Attendance ──
  if (attendanceApprovals > 0) {
    items.push({
      key: "attendance-approvals",
      group: "Attendance",
      tone: "half",
      title: `${plural(attendanceApprovals, "attendance request")} pending approval`,
      detail: "Overtime and attendance corrections waiting for your decision.",
      label: "Review",
      count: attendanceApprovals,
      onClick: () => navigate("/attendance/approvals"),
    });
  }
  const unmarked = attendance?.ok && attendance.available ? attendance.unmarked || 0 : 0;
  if (unmarked > 0) {
    items.push({
      key: "attendance-unmarked",
      group: "Attendance",
      tone: "half",
      title: `Attendance not marked today for ${plural(unmarked, "employee")}`,
      detail: "Mark today's attendance so payroll uses the right payable days.",
      label: "Mark",
      count: unmarked,
      onClick: () => navigate("/attendance/mark"),
    });
  }

  // ── Leave ──
  if (leave?.ok && leave.pending > 0) {
    items.push({
      key: "leave",
      group: "Leave",
      tone: "half",
      title: `${plural(leave.pending, "leave request")} pending approval`,
      detail: "Requires your review and approval.",
      label: "Review",
      count: leave.pending,
      onClick: () => navigate("/leave?tab=approval", { state: LEAVE_APPROVAL_NAV_STATE }),
    });
  }

  // ── Payroll ──
  if (readiness && readiness.complete === false && (readiness.total_steps || readiness.steps?.length)) {
    items.push({
      key: "payroll-setup",
      group: "Payroll",
      tone: "half",
      title: `Payroll setup is ${readiness.percent ?? 0}% complete`,
      detail: readiness.current_step_label
        ? `Next step: ${readiness.current_step_label}.`
        : "Finish payroll setup before running payroll.",
      label: "Continue",
      count: 1,
      onClick: () => navigate(readiness.current_step_route || "/payroll"),
    });
  }
  alerts
    .filter((a) => String(a.route || "").startsWith("/process-payroll"))
    .forEach((a, i) => {
      items.push({
        key: `payroll-${i}`,
        group: "Payroll",
        tone: a.level === "danger" ? "absent" : "half",
        title: a.text,
        detail: "Process and approve payroll for the period.",
        label: "Open",
        count: 1,
        onClick: () => navigate(a.route),
      });
    });

  // ── Compliance: every overdue / due-within-7-days statutory deadline of configured modules ──
  const configured = new Set(
    (overview.compliance_modules || []).filter((m) => m.configured !== false).map((m) => m.key)
  );
  (overview.compliance_deadlines || [])
    .filter((d) => (!configured.size || configured.has(d?.id)) && (d?.overdue || (d?.days_remaining ?? 99) <= 7))
    .sort((a, b) => (a.days_remaining ?? 99) - (b.days_remaining ?? 99))
    .forEach((d) => {
      const days = d.days_remaining ?? 0;
      const overdue = d.overdue || days < 0;
      items.push({
        key: `deadline-${d.id || d.name}`,
        group: "Compliance",
        tone: overdue ? "absent" : "half",
        title: overdue ? `${d.name} is overdue` : `${d.name} due in ${plural(days, "day")}`,
        detail: d.desc || "Upcoming statutory deadline.",
        label: "View",
        count: 1,
        onClick: () => navigate(d.route || "/statutory-reports"),
      });
    });

  return items;
}

/** Total for the "Pending Actions" tile. */
export function countActionItems(items) {
  return (items || []).reduce((sum, item) => sum + (item.count || 1), 0);
}
